import * as Sentry from '@sentry/react'
import { Map } from 'immutable'
import { AppDispatch } from 'source/js/data/store'

import appointmentsApi from '../../lyraTherapy/clients/clientDetails/data/appointmentsApi'

/**
 * Helper function for submitting charge status updates from payment modals.
 * Handles the common pattern of extracting description from form values,
 * mapping to reason text, and calling updateChargeStatus.
 *
 * @param values - Form values from the modal (Immutable Map object)
 * @param modalData - Modal data containing chargeId, payload, and is_too_frequent
 * @param reasonsMap - Map of reason keys to reason text (e.g., paymentReversalReasons)
 * @param updateChargeStatus - The bound updateChargeStatus action (from props/actions)
 * @returns Promise that resolves when the charge status update completes
 */
export const submitChargeStatusUpdate = (
  values: Map<string, any>,
  modalData: { chargeId: string; payload: Map<string, any>; is_too_frequent: boolean },
  reasonsMap: Record<string, string>,
  updateChargeStatus: (
    id: string,
    values: Map<string, any>,
    isTooFrequent: boolean,
    description?: string,
  ) => Promise<any>,
) => {
  let description = values.get('description')
  if (description === 'other') {
    description = values.get('description_text_area')
  } else {
    description = reasonsMap[description]
  }

  return updateChargeStatus(modalData.chargeId, modalData.payload, modalData.is_too_frequent, description)
}

/**
 * Reset DA Engage appointment and note after charge reversal.
 * This is specific to charge reversals and updates the appointment status via RTK Query.
 *
 * @param dispatch - Redux dispatch function (supports RTK Query thunks)
 * @param modalData - Object containing providerId and appointmentId
 */
export const resetDAEngageAppointmentAndNote = async (
  dispatch: AppDispatch,
  { providerId, appointmentId }: { providerId: string; appointmentId: number },
) => {
  try {
    await dispatch(appointmentsApi.endpoints.chargeReversal.initiate({ providerId, appointmentId })).unwrap()
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'Payments',
      data: {
        message: 'Failed to update appointment/note for charge reversal',
        appointmentId: appointmentId,
        providerUserId: providerId,
      },
    })
    Sentry.captureException(error)
    console.error('Failed to update appointment/note for charge reversal.', error)
  }
}
